import React, {Suspense} from "react";
import "./Tutorial.css";
import {useTypedSelector} from "../../hooks/useTypedSelector";

import T10Welcome from "./steps/T10Welcome";
import T20ClickBossOnMap from "./steps/T20ClickBossOnMap";
import T30BossWindow from "./steps/T30BossWindow";
import T35ShowRes from "./steps/T35ShowRes";
import T37ShowResOnCard from "./steps/T37ShowResOnCard";
import T40PlayFirstCard from "./steps/T40PlayFirstCard";
import T50PlaySecondCard from "./steps/T50PlaySecondCard";
import EndTurnBattle from "./steps/EndTurnBattle";
import T60SelectAttack from "./steps/T60SelectAttack";
import T70PlaySpellCard from "./steps/T70PlaySpellCard";
import T75SpellTarget from "./steps/T75SpellTarget";
import T80BattleFinish from "./steps/T80BattleFinish";
import T90MenuResearch from "./steps/T90MenuResearch";
import T95ClickOffers from "./steps/T95ClickOffers";
import T100ClickShop from "./steps/T100ClickShop";
import T110ClickDeck from "./steps/T110ClickDeck";
import T120ClickBuy from "./steps/T120ClickBuy";
import T130ClickAdd from "./steps/T130ClickAdd";
import T140ClickHome from "./steps/T140ClickHome";
import T150Final from "./steps/T150Final";
import T82BossWindowSecond from "./steps/T82BossWindowSecond";
import T84BossWindowSecond from "./steps/T84BossWindowSecond";
import {useActions} from "../../hooks/useActions";
import Helper from "../../services/Helper";
import T200ArenaStart from "./steps/T200ArenaStart";
import T210ArenaDaily from "./steps/T210ArenaDaily";
import T220ArenaChampion from "./steps/T220ArenaChampion";
import T230ArenaSeasonProgress from "./steps/T230ArenaSeasonProgress";
import T240ArenaFinish from "./steps/T240ArenaFinish";

type StepKey = string; // or a union of known strings if you want stricter typing

const STEP_COMPONENTS: Record<StepKey, React.FC | undefined> = {
    Welcome10: T10Welcome,
    ClickBossOnMap20: T20ClickBossOnMap,
    BossWindow30: T30BossWindow,
    showRes35: T35ShowRes,
    showRes37: T37ShowResOnCard,
    PlayFirstCard40: T40PlayFirstCard,
    PlaySecondCard50: T50PlaySecondCard,
    SelectAttack60: T60SelectAttack,
    PlaySpellCard70: T70PlaySpellCard,
    SpellTarget75: T75SpellTarget,
    BattleFinish80: T80BattleFinish,
    BossWindowSecond82: T82BossWindowSecond,
    BossWindowSecond84: T84BossWindowSecond,
    MenuResearch90: T90MenuResearch,
    ClickOffers95: T95ClickOffers,
    ClickShop100: T100ClickShop,
    ClickDeck110: T110ClickDeck,
    ClickBuy120: T120ClickBuy,
    ClickAdd130: T130ClickAdd,
    ClickHome140: T140ClickHome,
    Final150: T150Final,
    ArenaStart200: T200ArenaStart,
    ArenaDaily210: T210ArenaDaily,
    ArenaChampion220: T220ArenaChampion,
    ArenaSeasonProgress230: T230ArenaSeasonProgress,
    ArenaFinish240: T240ArenaFinish,

    // map all EndTurn variants to the same component
    EndTurnBattle45: EndTurnBattle,
    EndTurnBattle55: EndTurnBattle,
    EndTurnBattle65: EndTurnBattle,
};

function TutorialComponent() {
    const {tutorial} = useTypedSelector(state => state.auth);
    const {setTutorial, setRoute} = useActions();


    const step = useTypedSelector((s) => s.auth.tutorial.step);
    const isActive = useTypedSelector((s) => s.auth.tutorial.isActive);

    let Comp: React.FC | null = null;

    if (isActive && step) {
        Comp = STEP_COMPONENTS[step] ?? null;
    }

    const LAST_BATTLE_STEP = 'BattleFinish80';
    const MENU_RESEARCH_STEP = 'MenuResearch90';
    const FINAL_STEP = 'Final150';

    const ARENA_START_STEP = 'ArenaStart200';
    const ARENA_FINISH_STEP = 'ArenaFinish240';

    const currentOrder = Helper.getStepOrder(step);
    const battleOrder = Helper.getStepOrder(LAST_BATTLE_STEP);
    const menuResearchOrder = Helper.getStepOrder(MENU_RESEARCH_STEP);
    const finalOrder = Helper.getStepOrder(FINAL_STEP);
    const arenaStartOrder = Helper.getStepOrder(ARENA_START_STEP);
    const arenaFinishOrder = Helper.getStepOrder(ARENA_FINISH_STEP);

    const isBattlePhase = currentOrder <= battleOrder;

    const isResearchPhase =
        currentOrder >= menuResearchOrder && currentOrder < finalOrder;

    const isArenaPhase =
        currentOrder >= arenaStartOrder && currentOrder <= arenaFinishOrder;

    const isArenaLockedStep =
        step === ARENA_START_STEP || step === ARENA_FINISH_STEP;

    const canSkipArenaPhase = isArenaPhase && !isArenaLockedStep;

    const shouldShowSkip =
        isBattlePhase || isResearchPhase || canSkipArenaPhase;

    const handleSkip = () => {
        if (isBattlePhase) {
            setTutorial({
                ...tutorial,
                step: LAST_BATTLE_STEP,
                isActive: false,
            });
            return;
        }

        if (isResearchPhase) {
            setTutorial({
                ...tutorial,
                step: FINAL_STEP,
                isActive: true,
            });

            setRoute('home');
            return;
        }

        if (canSkipArenaPhase) {
            setTutorial({
                ...tutorial,
                step: ARENA_FINISH_STEP,
                isActive: true,
            });
        }
    };

    return (
        <div className="tutorialGlobalContainer">
            {Comp ? (
                <Suspense fallback={null}>
                    <Comp key={step}/>

                    {shouldShowSkip && (
                        <div
                            className="tutorialButtonSkip"
                            onClick={handleSkip}
                        />
                    )}
                </Suspense>
            ) : null}
        </div>
    );
}

export default TutorialComponent;
